import { useEffect, useMemo, useState } from 'react';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

const DEFAULT_OWNERS_LIMIT = 200;
const RECOMMENDATIONS_URL = '/gateway/api/v1/recommendations';

export type OwnerForFilter = {
	id: string;
	displayName: string;
	avatarUrl?: string;
};

type RecommendationUser = {
	id: string;
	name: string;
	entityType: string;
	avatarUrl?: string;
};

type RecommendationResponse = {
	recommendedUsers?: RecommendationUser[];
};

/**
 * Fetches recommended users for the current Confluence site via the Smarts
 * Recommendation Service (/gateway/api/v1/recommendations), using an empty
 * queryString and returns users in the recommended order (sorted in the backend).
 *
 * This is the same mechanism used by SmartUserPicker for its initial user list.
 * Returns users with displayName, accountId, and avatarUrl.
 * Used to populate the Content Owner filter dropdown.
 */
export const useOwnersForFilter = () => {
	const { cloudId } = useSessionData();
	const [owners, setOwners] = useState<OwnerForFilter[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<Error | undefined>(undefined);

	const requestBody = useMemo(
		() =>
			JSON.stringify({
				context: {
					contextType: 'confluence',
					siteId: cloudId,
					principalId: 'context',
					productKey: 'confluence',
				},
				includeUsers: true,
				includeGroups: false,
				includeTeams: false,
				maxNumberOfResults: DEFAULT_OWNERS_LIMIT,
				performSearchQueryOnly: false,
				searchQuery: {
					cpusQueryHighlights: {
						query: '',
						field: '',
					},
					customQuery: '',
					customerDirectoryId: '',
					filter: '',
					minimumAccessLevel: 'APPLICATION',
					queryString: '',
					restrictTo: {
						userIds: [],
						groupIds: [],
					},
					searchUserbase: false,
				},
			}),
		[cloudId],
	);

	useEffect(() => {
		if (!cloudId) {
			return;
		}

		let cancelled = false;
		setLoading(true);
		setError(undefined);

		fetch(RECOMMENDATIONS_URL, {
			method: 'POST',
			credentials: 'include',
			headers: {
				'content-type': 'application/json',
			},
			body: requestBody,
		})
			.then((response) => {
				if (!response.ok) {
					throw new Error(
						`Recommendations request failed: ${response.status} ${response.statusText}`,
					);
				}
				return response.json() as Promise<RecommendationResponse>;
			})
			.then((data) => {
				if (cancelled) {
					return;
				}
				const users = (data.recommendedUsers ?? []).filter(
					(user) => user.entityType === 'USER' && user.id && user.name,
				);
				setOwners(
					users.map((user) => ({
						id: user.id,
						displayName: user.name,
						avatarUrl: user.avatarUrl,
					})),
				);
			})
			.catch((err: unknown) => {
				if (!cancelled) {
					const caughtError = err instanceof Error ? err : new Error(String(err));
					setError(caughtError);
					getMonitoringClient().submitError(caughtError, {
						attribution: Attribution.PERFORMANCE_INSIGHTS,
					});
				}
			})
			.finally(() => {
				if (!cancelled) {
					setLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [cloudId, requestBody]);

	return {
		owners,
		loading,
		error,
	};
};
